import { useEffect, useState } from "react";
import { Auth } from "aws-amplify";
import axios from "axios";

// Every guide for a room, across all catalogs. The map needs the whole set at
// once — plotting only the first page of a long library would be misleading —
// so this walks the paged endpoint to the end. The walk is capped so a huge
// library can't spin forever.
//
// This only fetches the guides. Turning their addresses into map pins (the
// part that costs money) is left to the map, and only happens when someone
// opens it.
const MAX_PAGES = 5;
const PAGE_SIZE = 100;

export const useAllRoomGuides = (roomId) => {
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);

  useEffect(() => {
    if (!roomId) return undefined;

    let cancelled = false;

    const load = async () => {
      setLoading(true);

      try {
        const session = await Auth.currentSession();
        const idToken = session.getIdToken().getJwtToken();

        const headers = {
          Authorization: `Bearer ${idToken}`,
          "x-api-key": process.env.REACT_APP_LINKBASE_API_KEY,
        };

        const collected = [];
        let pageToken = null;

        for (let page = 0; page < MAX_PAGES; page += 1) {
          let url = `https://api.linkbase.ca/guides/by-catalog/all?roomId=${roomId}&limit=${PAGE_SIZE}`;
          if (pageToken) {
            url += `&nextPageToken=${encodeURIComponent(pageToken)}`;
          }

          // eslint-disable-next-line no-await-in-loop
          const response = await axios.get(url, { headers });

          const batch = response.data?.data?.guidesWithCatalogs || [];
          batch.forEach((item) => {
            collected.push({ ...item.guide, catalog: item.catalog });
          });

          pageToken = response.data?.data?.nextPageToken || null;
          if (!pageToken) break;
        }

        if (!cancelled) setGuides(collected);
      } catch (error) {
        console.error("Error fetching guides for map:", error);
      } finally {
        if (!cancelled) {
          setLoading(false);
          setFetched(true);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [roomId]);

  return { guides, loading, fetched };
};

export default useAllRoomGuides;
